<script setup lang="ts">
import { ArrowRight } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { useTagInjection } from '@/composables/useTagInjection'
import type { ModerationError } from '@/types'
import { ModerationRuleType } from '@/types'

interface Props {
	errors: ModerationError<Array<string[]> | string[]>[]
	mode?: 'generator' | 'tweak'
}

const props = defineProps<Props>()

const { t } = useI18n()

const { injectTag, injectMultipleTags } = useTagInjection(props.mode)

function getErrorStyles(type: ModerationRuleType) {
	const styles = {
		[ModerationRuleType.Suggestion]: {
			border: 'border-green-600/30',
			bg: 'bg-green-900/20',
			text: 'text-green-300',
		},
		[ModerationRuleType.Forbidden]: {
			border: 'border-red-600/30',
			bg: 'bg-red-900/20',
			text: 'text-red-300',
		},
	}
	return styles[type]
}

function getWordStyles(type: ModerationRuleType) {
	const styles = {
		[ModerationRuleType.Suggestion]: {
			bg: 'bg-green-800/30',
			border: 'border-green-600/40',
			text: 'text-green-200',
		},
		[ModerationRuleType.Forbidden]: {
			bg: 'bg-red-800/30',
			border: 'border-red-600/40',
			text: 'text-red-200',
		},
	}
	return styles[type]
}

function injectAll(tags: string[]): void {
	injectMultipleTags(tags)
}
</script>

<template>
	<div
		v-if="errors.length > 0"
		class="space-y-3"
	>
		<div
			v-for="(error, index) in errors"
			:key="index"
			class="border-2 border-solid rounded-lg p-2 transition-colors duration-200"
			:class="[
				getErrorStyles(error.type).border,
				getErrorStyles(error.type).bg,
			]"
		>
			<!-- Forbidden Words -->
			<div v-if="error.type !== ModerationRuleType.Suggestion">
				<div
					class="text-xs uppercase tracking-wide font-semibold mb-2 opacity-60"
					:class="getErrorStyles(error.type).text"
				>
					{{ t('moderation.banned_words_detected') }}
				</div>
				<div class="flex flex-wrap gap-1.5">
					<span
						v-for="word in error.words"
						:key="(word as string)"
						class="px-2 py-1 rounded text-xs font-medium border"
						:class="[
							getWordStyles(error.type).bg,
							getWordStyles(error.type).border,
							getWordStyles(error.type).text,
						]"
					>
						{{ word }}
					</span>
				</div>
				<div class="text-slate-300 text-xsp">
					{{ t('moderation.auto_deleted') }}
				</div>
			</div>

			<div
				v-if="error.type === ModerationRuleType.Suggestion"
				class="text-sm flex flex-col gap-1"
			>
				<div class="text-xs uppercase tracking-wide font-semibold mb-2 text-green-300 opacity-60">
					{{ t('moderation.suggestions') }}
				</div>
				<div
					v-for="(words, idx) in error.words"
					:key="idx"
					class="flex gap-2 items-center"
				>
					<span v-if="Array.isArray(words)">
						{{ words.join(', ') }}
					</span>
					<ArrowRight class="w-4 h-4" />
					<template v-if="error.suggestions && Array.isArray(error.suggestions[idx]) && error.suggestions[idx].length > 0">
						<span
							v-for="(suggestion, idx_sug) in error.suggestions[idx]"
							:key="idx_sug"
							class="px-2 py-1 rounded text-xs cursor-pointer select-none font-medium border-[1px] border-solid bg-green-800/30 border-green-600/40 text-green-200 hover:bg-green-700/40 transition-colors"
							@click="injectTag(suggestion)"
						>{{ suggestion }}</span>
					</template>
					<template v-if="error.suggestions && Array.isArray(error.suggestions[idx]) && error.suggestions[idx].length > 1">
						<Divider
							layout="vertical"
							class="mx-1 my-0"
						/>
						<span
							class="px-2 py-1 rounded text-xs cursor-pointer select-none font-medium border-[1px] border-solid bg-green-800/30 border-green-600/40 text-green-200 hover:bg-green-700/40 transition-colors"
							@click="injectAll((error.suggestions as string[][])[idx])"
						>
							{{ t('common.inject_all') }}
						</span>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>
